// Datums en bedragen, zoals de backend (src/crm/dates.ts): "vandaag" en uren in Europe/Brussels,
// datums als 'JJJJ-MM-DD', tijdstippen als ISO in UTC, opmaak in het Nederlands (België).

const TZ = "Europe/Brussels";
const DATE = /^\d{4}-\d{2}-\d{2}$/;

const dayFormat = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });

/** Datum in Brussel van een tijdstip ('JJJJ-MM-DD'). */
export const brusselsDate = (instant: Date) => dayFormat.format(instant);
export const todayIso = () => brusselsDate(new Date());

/** De kalenderdag van een datum of tijdstip (tijdstippen worden eerst naar Brussel omgezet). */
export function dayOf(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  if (DATE.test(value)) return value;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : brusselsDate(instant);
}

const noon = (day: string) => new Date(`${day}T12:00:00Z`);

export function addDays(day: string, days: number) {
  const d = noon(day);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** De maandag van de week van een datum ('JJJJ-MM-DD'). */
export function startOfWeek(day: string) {
  const weekday = (noon(day).getUTCDay() + 6) % 7; // maandag = 0
  return addDays(day, -weekday);
}

/** Dagen van vandaag tot de datum (negatief = voorbij), of null zonder datum. */
export function daysUntil(value: unknown): number | null {
  const day = dayOf(value);
  if (!day) return null;
  return Math.round((noon(day).getTime() - noon(todayIso()).getTime()) / 86_400_000);
}

const formats = {
  short: new Intl.DateTimeFormat("nl-BE", { day: "numeric", month: "short", timeZone: "UTC" }),
  medium: new Intl.DateTimeFormat("nl-BE", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }),
  long: new Intl.DateTimeFormat("nl-BE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }),
  weekday: new Intl.DateTimeFormat("nl-BE", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })
};
const timeFormat = new Intl.DateTimeFormat("nl-BE", { hour: "2-digit", minute: "2-digit", timeZone: TZ });

/** "6 okt 2026" (medium), "6 okt" (short), "6 oktober 2026" (long). */
export function fmtDate(value: unknown, style: "short" | "medium" | "long" = "medium") {
  const day = dayOf(value);
  return day ? formats[style].format(noon(day)) : "—";
}

export const weekdayLabel = (value: unknown) => {
  const day = dayOf(value);
  return day ? formats.weekday.format(noon(day)) : "";
};

/** Uur in Brussel van een tijdstip, bv. "10:00". */
export function fmtTime(value: unknown) {
  if (typeof value !== "string" || DATE.test(value)) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : timeFormat.format(d);
}

export const fmtDateTime = (value: unknown) => (dayOf(value) ? `${fmtDate(value, "short")} · ${fmtTime(value)}` : "—");

/** "Vandaag", "Morgen", "over 3 d.", "5 d. geleden". */
export function relativeDay(value: unknown) {
  const days = daysUntil(value);
  if (days === null) return "—";
  if (days === 0) return "Vandaag";
  if (days === 1) return "Morgen";
  if (days === -1) return "Gisteren";
  return days < 0 ? `${Math.abs(days)} d. geleden` : `over ${days} d.`;
}

const money = new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const moneyCents = new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "€ 2.450" (zonder centen) of met `cents` "€ 2.450,00". */
export function fmtMoney(value: unknown, cents = false) {
  const n = Number(value);
  return value === undefined || value === null || value === "" || !Number.isFinite(n) ? "—" : (cents ? moneyCents : money).format(n);
}
